import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../../lib/supabase';
import MainLayout from '../../components/layout/MainLayout';
import Icon from '../../components/AppIcon';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area } from 'recharts';
import PerformanceAnalyticsTab from '../project-reports/components/PerformanceAnalyticsTab';
import ResourceUtilizationTab from '../project-reports/components/ResourceUtilizationTab';
import BudgetAnalysisTab from '../project-reports/components/BudgetAnalysisTab';
import DeliverableTrackingTab from '../project-reports/components/DeliverableTrackingTab';
import PMReportingTab from '../project-reports/components/PMReportingTab';

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4'];

const StatCard = ({ title, value, subtitle, icon, color = 'blue' }) => {
  const colorMap = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    yellow: 'bg-yellow-50 text-yellow-600',
    purple: 'bg-purple-50 text-purple-600',
    red: 'bg-red-50 text-red-600',
    cyan: 'bg-cyan-50 text-cyan-600',
  };
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 flex items-start gap-4 shadow-sm">
      <div className={`p-3 rounded-lg ${colorMap?.[color]}`}>
        <Icon name={icon} size={22} />
      </div>
      <div>
        <p className="text-sm text-gray-500 font-medium">{title}</p>
        <p className="text-2xl font-bold text-gray-900 mt-0.5">{value}</p>
        {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
      </div>
    </div>
  );
};

const SectionCard = ({ title, children, action = null }) => (
  <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
    <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
      <h3 className="font-semibold text-gray-800 text-base">{title}</h3>
      {action}
    </div>
    <div className="p-6">{children}</div>
  </div>
);

const Analytics = () => {
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [bids, setBids] = useState([]);
  const [resources, setResources] = useState([]);
  const [customers, setCustomers] = useState([]);

  // Filters
  const [dateRange, setDateRange] = useState('all');
  const [projectStatusFilter, setProjectStatusFilter] = useState('all');
  const [bidStatusFilter, setBidStatusFilter] = useState('all');
  const [resourceCategoryFilter, setResourceCategoryFilter] = useState('all');

  // Project Reports date range (separate)
  const [reportDateRange, setReportDateRange] = useState('30d');

  // Table sort
  const [projectSort, setProjectSort] = useState({ key: 'project_name', dir: 'asc' });
  const [bidSort, setBidSort] = useState({ key: 'total_amount', dir: 'desc' });

  // Active tab
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [{ data: p }, { data: b }, { data: r }, { data: c }] = await Promise.all([
        supabase?.from('projects')?.select('*')?.order('created_at', { ascending: false }),
        supabase?.from('bids')?.select('*')?.order('created_date', { ascending: false }),
        supabase?.from('resources')?.select('*, assignments:resource_assignments(*)')?.order('created_at', { ascending: false }),
        supabase?.from('customers')?.select('*')?.order('created_at', { ascending: false }),
      ]);
      setProjects(p || []);
      setBids(b || []);
      setResources(r || []);
      setCustomers(c || []);
    } catch (err) {
      console.error('Analytics fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Date filter helper
  const isInDateRange = (dateStr) => {
    if (dateRange === 'all' || !dateStr) return true;
    const date = new Date(dateStr);
    const now = new Date();
    if (dateRange === '30d') return (now - date) / (1000 * 60 * 60 * 24) <= 30;
    if (dateRange === '90d') return (now - date) / (1000 * 60 * 60 * 24) <= 90;
    if (dateRange === '1y') return (now - date) / (1000 * 60 * 60 * 24) <= 365;
    return true;
  };

  // Filtered data
  const filteredProjects = useMemo(() => {
    return projects?.filter(p =>
      (projectStatusFilter === 'all' || p?.status === projectStatusFilter) &&
      isInDateRange(p?.created_at)
    );
  }, [projects, projectStatusFilter, dateRange]);

  const filteredBids = useMemo(() => {
    return bids?.filter(b =>
      (bidStatusFilter === 'all' || b?.status === bidStatusFilter) &&
      isInDateRange(b?.created_date)
    );
  }, [bids, bidStatusFilter, dateRange]);

  const filteredResources = useMemo(() => {
    return resources?.filter(r =>
      resourceCategoryFilter === 'all' || r?.category === resourceCategoryFilter
    );
  }, [resources, resourceCategoryFilter]);

  // ---- KPI Stats ----
  const totalRevenue = useMemo(() =>
    filteredBids?.filter(b => b?.status === 'Accepted' || b?.status?.includes('Converted'))?.reduce((sum, b) => sum + parseFloat(b?.total_amount || 0), 0),
    [filteredBids]
  );

  const conversionRate = useMemo(() => {
    const submitted = filteredBids?.filter(b => b?.status !== 'Draft')?.length;
    const accepted = filteredBids?.filter(b => b?.status === 'Accepted' || b?.status?.includes('Converted'))?.length;
    return submitted > 0 ? ((accepted / submitted) * 100)?.toFixed(1) : 0;
  }, [filteredBids]);

  const resourceUtilization = useMemo(() => {
    const inUse = filteredResources?.filter(r => r?.status === 'In Use')?.length;
    return filteredResources?.length > 0 ? ((inUse / filteredResources?.length) * 100)?.toFixed(0) : 0;
  }, [filteredResources]);

  // ---- Chart Data ----
  const projectsByStatus = useMemo(() => {
    const counts = {};
    filteredProjects?.forEach(p => { counts[p.status] = (counts?.[p?.status] || 0) + 1; });
    return Object.entries(counts)?.map(([name, value]) => ({ name, value }));
  }, [filteredProjects]);

  const bidsByStatus = useMemo(() => {
    const counts = {};
    filteredBids?.forEach(b => { counts[b.status] = (counts?.[b?.status] || 0) + 1; });
    return Object.entries(counts)?.map(([name, value]) => ({ name, value }));
  }, [filteredBids]);

  const resourcesByCategory = useMemo(() => {
    const counts = {};
    filteredResources?.forEach(r => {
      const cat = r?.category || 'Other';
      if (!counts?.[cat]) counts[cat] = { name: cat, total: 0, inUse: 0, available: 0 };
      counts[cat].total++;
      if (r?.status === 'In Use') counts[cat].inUse++;
      if (r?.status === 'Available') counts[cat].available++;
    });
    return Object.values(counts);
  }, [filteredResources]);

  const monthlyBidRevenue = useMemo(() => {
    const monthly = {};
    filteredBids?.forEach(b => {
      if (!b?.created_date) return;
      const month = new Date(b.created_date)?.toLocaleString('default', { month: 'short', year: '2-digit' });
      if (!monthly?.[month]) monthly[month] = { month, revenue: 0, count: 0 };
      monthly[month].revenue += parseFloat(b?.total_amount || 0);
      monthly[month].count++;
    });
    return Object.values(monthly)?.slice(-12);
  }, [filteredBids]);

  const projectCompletionData = useMemo(() => {
    return filteredProjects?.filter(p => p?.completion_percentage !== null && p?.completion_percentage !== undefined)?.map(p => ({
        name: p?.project_name?.length > 18 ? p?.project_name?.slice(0, 18) + '\u2026' : p?.project_name,
        completion: parseFloat(p?.completion_percentage || 0),
        status: p?.status,
      }))?.sort((a, b) => b?.completion - a?.completion)?.slice(0, 10);
  }, [filteredProjects]);

  // ---- Sorted Tables ----
  const sortedProjects = useMemo(() => {
    return [...filteredProjects]?.sort((a, b) => {
      const av = a?.[projectSort?.key] ?? '';
      const bv = b?.[projectSort?.key] ?? '';
      if (typeof av === 'number') return projectSort?.dir === 'asc' ? av - bv : bv - av;
      return projectSort?.dir === 'asc'
        ? String(av)?.localeCompare(String(bv))
        : String(bv)?.localeCompare(String(av));
    });
  }, [filteredProjects, projectSort]);

  const sortedBids = useMemo(() => {
    return [...filteredBids]?.sort((a, b) => {
      const av = a?.[bidSort?.key] ?? '';
      const bv = b?.[bidSort?.key] ?? '';
      if (bidSort?.key === 'total_amount') return bidSort?.dir === 'asc' ? parseFloat(av) - parseFloat(bv) : parseFloat(bv) - parseFloat(av);
      return bidSort?.dir === 'asc'
        ? String(av)?.localeCompare(String(bv))
        : String(bv)?.localeCompare(String(av));
    });
  }, [filteredBids, bidSort]);

  const handleProjectSort = (key) => {
    setProjectSort(prev => ({ key, dir: prev?.key === key && prev?.dir === 'asc' ? 'desc' : 'asc' }));
  };

  const handleBidSort = (key) => {
    setBidSort(prev => ({ key, dir: prev?.key === key && prev?.dir === 'asc' ? 'desc' : 'asc' }));
  };

  const SortIcon = ({ col, current }) => (
    <Icon
      name={current?.key === col ? (current?.dir === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'}
      size={14}
      className="inline ml-1 text-gray-400"
    />
  );

  const statusBadge = (status) => {
    const map = {
      'In Progress': 'bg-blue-100 text-blue-700',
      'Planning': 'bg-yellow-100 text-yellow-700',
      'Completed': 'bg-green-100 text-green-700',
      'On Hold': 'bg-gray-100 text-gray-600',
      'Accepted': 'bg-green-100 text-green-700',
      'Draft': 'bg-gray-100 text-gray-600',
      'Submitted': 'bg-blue-100 text-blue-700',
      'Rejected': 'bg-red-100 text-red-700',
      'Available': 'bg-green-100 text-green-700',
      'In Use': 'bg-blue-100 text-blue-700',
      'In Maintenance': 'bg-yellow-100 text-yellow-700',
    };
    const cls = map?.[status] || 'bg-gray-100 text-gray-600';
    return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>{status}</span>;
  };

  const reportDateRangeOptions = [
    { value: '7d', label: 'Last 7 Days' },
    { value: '30d', label: 'Last 30 Days' },
    { value: '90d', label: 'Last 90 Days' },
    { value: '1y', label: 'Last Year' },
    { value: 'all', label: 'All Time' }
  ];

  // Tab groups
  const tabGroups = [
    {
      label: 'Analytics',
      tabs: [
        { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
        { id: 'projects', label: 'Projects', icon: 'Briefcase' },
        { id: 'bids', label: 'Bids', icon: 'FileText' },
        { id: 'resources', label: 'Resources', icon: 'Package' },
      ]
    },
    {
      label: 'Project Reports',
      tabs: [
        { id: 'performance', label: 'Performance Analytics', icon: 'TrendingUp' },
        { id: 'resource-utilization', label: 'Resource Utilization', icon: 'Package' },
        { id: 'budget', label: 'Budget Analysis', icon: 'DollarSign' },
        { id: 'deliverables', label: 'Deliverable Tracking', icon: 'ListTodo' },
        { id: 'pm-reporting', label: 'PM Reporting', icon: 'UserCheck' },
      ]
    }
  ];

  const isReportTab = ['performance', 'resource-utilization', 'budget', 'deliverables', 'pm-reporting']?.includes(activeTab);

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <div className="flex flex-col items-center gap-3">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
            <p className="text-gray-500 text-sm">Loading analytics data&hellip;</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="p-6 space-y-6 max-w-screen-2xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Analytics &amp; Reporting</h1>
            <p className="text-sm text-gray-500 mt-1">Real-time insights across projects, bids, resources, and performance reports</p>
          </div>
          <div className="flex items-center gap-2">
            {isReportTab ? (
              <>
                <Icon name="Calendar" size={16} className="text-gray-400" />
                <select
                  value={reportDateRange}
                  onChange={e => setReportDateRange(e?.target?.value)}
                  className="text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {reportDateRangeOptions?.map(opt => (
                    <option key={opt?.value} value={opt?.value}>{opt?.label}</option>
                  ))}
                </select>
              </>
            ) : (
              <>
                <Icon name="Calendar" size={16} className="text-gray-400" />
                <select
                  value={dateRange}
                  onChange={e => setDateRange(e?.target?.value)}
                  className="text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Time</option>
                  <option value="30d">Last 30 Days</option>
                  <option value="90d">Last 90 Days</option>
                  <option value="1y">Last Year</option>
                </select>
                <button
                  onClick={fetchAll}
                  className="flex items-center gap-1.5 text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white hover:bg-gray-50 transition-colors"
                >
                  <Icon name="RefreshCw" size={14} />
                  Refresh
                </button>
              </>
            )}
          </div>
        </div>

        {/* KPI Summary Cards - only for analytics tabs */}
        {!isReportTab && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard title="Total Projects" value={filteredProjects?.length} subtitle={`${filteredProjects?.filter(p => p?.status === 'In Progress')?.length} in progress`} icon="Briefcase" color="blue" />
            <StatCard title="Total Bids" value={filteredBids?.length} subtitle={`${filteredBids?.filter(b => b?.status === 'Draft')?.length} drafts`} icon="FileText" color="purple" />
            <StatCard title="Bid Conversion" value={`${conversionRate}%`} subtitle="Accepted / Submitted" icon="TrendingUp" color="green" />
            <StatCard title="Resource Utilization" value={`${resourceUtilization}%`} subtitle={`${filteredResources?.filter(r => r?.status === 'In Use')?.length} of ${filteredResources?.length} in use`} icon="Package" color="yellow" />
          </div>
        )}

        {/* Grouped Tabs */}
        <div className="border-b border-gray-200">
          <div className="flex gap-6 overflow-x-auto">
            {tabGroups?.map((group, gi) => (
              <div key={gi} className="flex flex-col">
                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-1 mb-1">{group?.label}</span>
                <div className="flex gap-0.5">
                  {group?.tabs?.map(tab => (
                    <button
                      key={tab?.id}
                      onClick={() => setActiveTab(tab?.id)}
                      className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-all whitespace-nowrap relative ${
                        activeTab === tab?.id
                          ? 'text-blue-600 border-b-2 border-blue-600' :'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <Icon name={tab?.icon} size={15} />
                      {tab?.label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ===== OVERVIEW TAB ===== */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Projects by Status Pie */}
              <SectionCard title="Projects by Status">
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie data={projectsByStatus} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={({ name, percent }) => `${name} ${(percent * 100)?.toFixed(0)}%`}>
                      {projectsByStatus?.map((_, i) => <Cell key={i} fill={COLORS?.[i % COLORS?.length]} />)}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </SectionCard>

              {/* Bids by Status Pie */}
              <SectionCard title="Bids by Status">
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie data={bidsByStatus} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={({ name, percent }) => `${name} ${(percent * 100)?.toFixed(0)}%`}>
                      {bidsByStatus?.map((_, i) => <Cell key={i} fill={COLORS?.[i % COLORS?.length]} />)}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </SectionCard>
            </div>

            {/* Monthly Bid Revenue Area Chart */}
            <SectionCard title="Monthly Bid Revenue Trend">
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={monthlyBidRevenue}>
                  <defs>
                    <linearGradient id="revenueGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} tickFormatter={v => `$${(v / 1000)?.toFixed(0)}k`} />
                  <Tooltip formatter={v => [`$${parseFloat(v)?.toLocaleString()}`, 'Revenue']} />
                  <Area type="monotone" dataKey="revenue" stroke="#3B82F6" fill="url(#revenueGrad)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </SectionCard>

            {/* Resource Utilization by Category */}
            <SectionCard title="Resource Utilization by Category">
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={resourcesByCategory}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="inUse" name="In Use" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="available" name="Available" fill="#10B981" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="total" name="Total" fill="#E5E7EB" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </SectionCard>
          </div>
        )}

        {/* ===== PROJECTS TAB ===== */}
        {activeTab === 'projects' && (
          <div className="space-y-6">
            <div className="flex flex-wrap gap-3 items-center">
              <div className="flex items-center gap-2">
                <Icon name="Filter" size={15} className="text-gray-400" />
                <span className="text-sm text-gray-500 font-medium">Filter:</span>
              </div>
              <select
                value={projectStatusFilter}
                onChange={e => setProjectStatusFilter(e?.target?.value)}
                className="text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Statuses</option>
                {[...new Set(projects.map(p => p.status))]?.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <span className="text-sm text-gray-400">{filteredProjects?.length} results</span>
            </div>

            <SectionCard title="Project Completion Progress (Top 10)">
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={projectCompletionData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 12 }} tickFormatter={v => `${v}%`} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={140} />
                  <Tooltip formatter={v => [`${v}%`, 'Completion']} />
                  <Bar dataKey="completion" fill="#3B82F6" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </SectionCard>

            <SectionCard title="Projects Detail Table">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100">
                      {[
                        { key: 'project_name', label: 'Project Name' },
                        { key: 'project_number', label: 'Number' },
                        { key: 'status', label: 'Status' },
                        { key: 'customer_name', label: 'Customer' },
                        { key: 'completion_percentage', label: 'Completion' },
                        { key: 'start_date', label: 'Start Date' },
                        { key: 'end_date', label: 'End Date' },
                      ]?.map(col => (
                        <th
                          key={col?.key}
                          onClick={() => handleProjectSort(col?.key)}
                          className="text-left py-3 px-3 font-semibold text-gray-600 cursor-pointer hover:text-blue-600 select-none whitespace-nowrap"
                        >
                          {col?.label}<SortIcon col={col?.key} current={projectSort} />
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedProjects?.length === 0 ? (
                      <tr><td colSpan={7} className="text-center py-8 text-gray-400">No projects found</td></tr>
                    ) : sortedProjects?.map(p => (
                      <tr key={p?.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                        <td className="py-3 px-3 font-medium text-gray-800">{p?.project_name}</td>
                        <td className="py-3 px-3 text-gray-500">{p?.project_number}</td>
                        <td className="py-3 px-3">{statusBadge(p?.status)}</td>
                        <td className="py-3 px-3 text-gray-600">{p?.customer_name || '\u2014'}</td>
                        <td className="py-3 px-3">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 bg-gray-100 rounded-full h-1.5 min-w-16">
                              <div className="bg-blue-500 h-1.5 rounded-full" style={{ width: `${p?.completion_percentage || 0}%` }} />
                            </div>
                            <span className="text-xs text-gray-500 w-8">{p?.completion_percentage || 0}%</span>
                          </div>
                        </td>
                        <td className="py-3 px-3 text-gray-500">{p?.start_date ? new Date(p.start_date)?.toLocaleDateString() : '\u2014'}</td>
                        <td className="py-3 px-3 text-gray-500">{p?.end_date ? new Date(p.end_date)?.toLocaleDateString() : '\u2014'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </SectionCard>
          </div>
        )}

        {/* ===== BIDS TAB ===== */}
        {activeTab === 'bids' && (
          <div className="space-y-6">
            <div className="flex flex-wrap gap-3 items-center">
              <div className="flex items-center gap-2">
                <Icon name="Filter" size={15} className="text-gray-400" />
                <span className="text-sm text-gray-500 font-medium">Filter:</span>
              </div>
              <select
                value={bidStatusFilter}
                onChange={e => setBidStatusFilter(e?.target?.value)}
                className="text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Statuses</option>
                {[...new Set(bids.map(b => b.status))]?.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <span className="text-sm text-gray-400">{filteredBids?.length} results</span>
            </div>

            <SectionCard title="Monthly Bid Volume &amp; Revenue">
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={monthlyBidRevenue}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                  <YAxis yAxisId="left" tick={{ fontSize: 12 }} tickFormatter={v => `$${(v / 1000)?.toFixed(0)}k`} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} />
                  <Tooltip formatter={(v, name) => name === 'Revenue' ? [`$${parseFloat(v)?.toLocaleString()}`, name] : [v, name]} />
                  <Legend />
                  <Bar yAxisId="left" dataKey="revenue" name="Revenue" fill="#8B5CF6" radius={[4, 4, 0, 0]} />
                  <Bar yAxisId="right" dataKey="count" name="Count" fill="#06B6D4" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </SectionCard>

            <SectionCard title="Bids Detail Table">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100">
                      {[
                        { key: 'bid_number', label: 'Bid #' },
                        { key: 'project_name', label: 'Project' },
                        { key: 'customer_name', label: 'Customer' },
                        { key: 'status', label: 'Status' },
                        { key: 'total_amount', label: 'Amount' },
                        { key: 'created_date', label: 'Created' },
                      ]?.map(col => (
                        <th
                          key={col?.key}
                          onClick={() => handleBidSort(col?.key)}
                          className="text-left py-3 px-3 font-semibold text-gray-600 cursor-pointer hover:text-blue-600 select-none whitespace-nowrap"
                        >
                          {col?.label}<SortIcon col={col?.key} current={bidSort} />
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedBids?.length === 0 ? (
                      <tr><td colSpan={6} className="text-center py-8 text-gray-400">No bids found</td></tr>
                    ) : sortedBids?.map(b => (
                      <tr key={b?.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                        <td className="py-3 px-3 font-medium text-gray-800">{b?.bid_number}</td>
                        <td className="py-3 px-3 text-gray-700">{b?.project_name}</td>
                        <td className="py-3 px-3 text-gray-600">{b?.customer_name || '\u2014'}</td>
                        <td className="py-3 px-3">{statusBadge(b?.status)}</td>
                        <td className="py-3 px-3 font-semibold text-gray-800">${parseFloat(b?.total_amount || 0)?.toLocaleString()}</td>
                        <td className="py-3 px-3 text-gray-500">{b?.created_date ? new Date(b.created_date)?.toLocaleDateString() : '\u2014'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </SectionCard>
          </div>
        )}

        {/* ===== RESOURCES TAB ===== */}
        {activeTab === 'resources' && (
          <div className="space-y-6">
            <div className="flex flex-wrap gap-3 items-center">
              <div className="flex items-center gap-2">
                <Icon name="Filter" size={15} className="text-gray-400" />
                <span className="text-sm text-gray-500 font-medium">Filter:</span>
              </div>
              <select
                value={resourceCategoryFilter}
                onChange={e => setResourceCategoryFilter(e?.target?.value)}
                className="text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Categories</option>
                {[...new Set(resources.map(r => r.category).filter(Boolean))]?.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <span className="text-sm text-gray-400">{filteredResources?.length} results</span>
            </div>

            <SectionCard title="Resource Status by Category">
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={resourcesByCategory}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="inUse" name="In Use" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="available" name="Available" fill="#10B981" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="total" name="Total" fill="#E5E7EB" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </SectionCard>

            <SectionCard title="Resources Detail Table">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100">
                      <th className="text-left py-3 px-3 font-semibold text-gray-600">Name</th>
                      <th className="text-left py-3 px-3 font-semibold text-gray-600">Category</th>
                      <th className="text-left py-3 px-3 font-semibold text-gray-600">Status</th>
                      <th className="text-left py-3 px-3 font-semibold text-gray-600">Type</th>
                      <th className="text-left py-3 px-3 font-semibold text-gray-600">Assignments</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredResources?.length === 0 ? (
                      <tr><td colSpan={5} className="text-center py-8 text-gray-400">No resources found</td></tr>
                    ) : filteredResources?.map(r => (
                      <tr key={r?.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                        <td className="py-3 px-3 font-medium text-gray-800">{r?.name}</td>
                        <td className="py-3 px-3 text-gray-600 capitalize">{r?.category || '\u2014'}</td>
                        <td className="py-3 px-3">{statusBadge(r?.status)}</td>
                        <td className="py-3 px-3 text-gray-500 capitalize">{r?.type || '\u2014'}</td>
                        <td className="py-3 px-3">
                          <span className="inline-flex items-center gap-1 text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                            <Icon name="Link" size={11} />
                            {r?.assignments?.length || 0}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </SectionCard>
          </div>
        )}

        {/* ===== PROJECT REPORTS TABS ===== */}
        {activeTab === 'performance' && <PerformanceAnalyticsTab dateRange={reportDateRange} />}
        {activeTab === 'resource-utilization' && <ResourceUtilizationTab dateRange={reportDateRange} />}
        {activeTab === 'budget' && <BudgetAnalysisTab dateRange={reportDateRange} />}
        {activeTab === 'deliverables' && <DeliverableTrackingTab dateRange={reportDateRange} />}
        {activeTab === 'pm-reporting' && <PMReportingTab />}
      </div>
    </MainLayout>
  );
};

export default Analytics;
